import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { NetInfoState, useNetInfo } from '@react-native-community/netinfo';
import { isNil } from 'lodash-es';

import { useConfigValue } from 'hooks/configs/use-config-value';
import { getCurrentSession } from 'remote/auth';
import { QUERY_TYPE, getGraphqlApiUrl } from 'remote/constants';
import { NetworkConnectionError } from 'remote/exceptions';
import {
  getSanityGraphqlQueryUrl,
  getSanityGraphqlQueryUrlV2,
  getSanityGroqQueryUrl,
} from 'remote/index';
import { staticSessionId } from 'state/amplitude';
import { useErrorContext } from 'state/errors';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { sanityDataset as defaultSanityDataset } from 'utils/environment';
import logger from 'utils/logger';
import { FetchConfig, fetch } from 'utils/network';
import { useMemoAll } from 'utils/use-memo-all';

import {
  REACHABILITY_SHORT_TIMEOUT,
  addNetworkStatusListener,
  staticConnectionStatus,
} from './status';

export interface QueryConfig<TResult, TVars> extends Omit<FetchConfig<TResult, TVars>, 'language'> {
  queryType: QUERY_TYPE;
}

export type Query = <TResult, TVars>(o: QueryConfig<TResult, TVars>) => Promise<TResult>;

export default function useNetwork() {
  const { language, region } = useLocale();

  const { handleError } = useErrorContext();

  const useSanityCdn = useFlag(LaunchDarklyFlag.ENABLE_SANITY_CDN);
  const sanityProjectId = useConfigValue({ key: 'sanityProjectId', isRegionalized: false });

  const [isInternetReachable, setIsInternetReachable] = useState(true);

  const { isConnected } = useNetInfo();
  const notifyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    function checkStatus(info: NetInfoState) {
      if (info.isInternetReachable) {
        setIsInternetReachable(true);
      }

      if (info.isInternetReachable && notifyTimeoutRef.current) {
        clearTimeout(notifyTimeoutRef.current);
        notifyTimeoutRef.current = null;
      }

      // delay letting UI know about a failed health check to reduce potential spam of messages / alerts
      if (!info.isInternetReachable && !notifyTimeoutRef.current) {
        notifyTimeoutRef.current = setTimeout(() => {
          setIsInternetReachable(false);
        }, REACHABILITY_SHORT_TIMEOUT * 3);
      }
    }

    checkStatus(staticConnectionStatus.current);

    const cleanUp = addNetworkStatusListener(checkStatus);

    return () => {
      notifyTimeoutRef.current && clearTimeout(notifyTimeoutRef.current);
      cleanUp();
    };
  }, []);

  const sanityEndpoints = useMemo(() => {
    // use cdn by default if LD flag is null or undefined
    const useCdn = isNil(useSanityCdn) ? true : useSanityCdn;

    // TODO: RN - locale switching -- shouldn't this just pull from config?
    const sanityDataset = region
      ? `${defaultSanityDataset()}_${region.toLowerCase()}`
      : defaultSanityDataset().toLowerCase();

    const groq = getSanityGroqQueryUrl({
      sanityDataset,
      useCdn,
      sanityProjectId,
    });
    const graphql = getSanityGraphqlQueryUrl({
      sanityDataset,
      useCdn,
      sanityProjectId,
    });
    const graphqlV2 = getSanityGraphqlQueryUrlV2({
      sanityDataset,
      useCdn,
      sanityProjectId,
    });

    return {
      graphql,
      groq,
      graphqlV2,
    };
  }, [region, sanityProjectId, useSanityCdn]);

  const query = useCallback<Query>(
    ({ queryType, ...config }) => {
      const getEndpointUrl = (queryType: QUERY_TYPE) => {
        switch (queryType) {
          case QUERY_TYPE.LambdaGraphqlQuery:
            return getGraphqlApiUrl();
          case QUERY_TYPE.SanityGraphqlQuery:
            return sanityEndpoints.graphql;
          case QUERY_TYPE.SanityGraphqlV2Query:
            return sanityEndpoints.graphqlV2;
          case QUERY_TYPE.SanityGroqQuery:
            return sanityEndpoints.groq;
          default:
            throw new Error('Unknown Query Type');
        }
      };

      return getCurrentSession().then(userSession => {
        const token = userSession ? userSession.getIdToken().getJwtToken() : '';
        const headers: { [key: string]: string } = {
          'Content-Type': 'application/json',
          ...config.headers,
        };

        const endpoint = getEndpointUrl(queryType);

        if (queryType === QUERY_TYPE.LambdaGraphqlQuery && token) {
          headers.Authorization = `Bearer ${token}`;
        }

        return fetch(endpoint, {
          ...config,
          connection: staticConnectionStatus.current,
          headers,
          language,
          region,
          sessionId: staticSessionId.current,
        }).catch((err: Error | NetworkConnectionError) => {
          const jwtExpiredError = 'JWT is invalid or expired';
          if (err.message.includes(jwtExpiredError)) {
            logger.warn({
              message: jwtExpiredError,
            });
          }

          if (Object.prototype.hasOwnProperty.call(err, 'connectionError')) {
            logger.error({ error: err, message: 'Network connection error' });
            throw err;
          }
          handleError(err);
          throw err;
        });
      });
    },
    [
      sanityEndpoints.graphql,
      sanityEndpoints.graphqlV2,
      sanityEndpoints.groq,
      language,
      region,
      handleError,
    ]
  );

  const isNetworkConnected = isConnected !== false;

  const value = useMemoAll({
    query,
    sanityEndpoints,
    isInternetReachable,
    isNetworkConnected,
  });

  return value;
}
